import { createFileRoute } from "@tanstack/react-router";
import { Mail, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { useSession } from "../../auth-client";
import { TitleBar } from "../../components/TitleBar";
import { useProfile, useProfileActions } from "../../profile/use-profile";

export const Route = createFileRoute("/_workspace/me")({
  head: () => ({ meta: [{ title: "Profile" }] }),
  component: ProfilePage,
});

/** Human label for the sign-in method carried on the session. */
function providerLabel(provider: "google" | "dev" | undefined): string {
  switch (provider) {
    case "google":
      return "Google";
    case "dev":
      return "Dev sign-in";
    default:
      return "Unknown";
  }
}

function ProfilePage() {
  const profile = useProfile();
  const { data: session } = useSession();

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TitleBar compactOnly>
        <span className="text-sm font-medium">Profile</span>
      </TitleBar>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl px-6 pt-8 pb-12">
          <h1 className="text-lg font-semibold">Profile</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your name and email addresses apply across every space you belong
            to.
          </p>

          {profile.isLoading ? (
            <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
          ) : profile.error ? (
            <p className="mt-6 text-sm text-destructive">
              {(profile.error as Error).message}
            </p>
          ) : profile.data ? (
            <div className="mt-8 flex flex-col gap-10">
              {/* The editor shows the raw registry value — blank when unset or
                  cleared — so `null` is authoritative here. Display surfaces (menu,
                  presence, Heath's roster) fall back to the IdP name as the
                  default; the editor deliberately doesn't. */}
              <NameSection initial={profile.data.name ?? ""} />
              <LoginSection
                email={profile.data.email}
                image={session?.user.image ?? null}
                provider={session?.user.provider}
              />
              <EmailsSection
                primary={profile.data.email}
                additional={profile.data.additionalEmails}
                pending={profile.data.pendingEmails}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Editable display name. */
function NameSection({ initial }: { initial: string }) {
  const { saveName } = useProfileActions();
  const [value, setValue] = useState(initial);
  const trimmed = value.trim();
  const dirty = trimmed !== initial.trim();

  return (
    <section>
      <h2 className="text-sm font-semibold">Name</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        How Heath and other members see you.
      </p>
      <form
        className="mt-3 flex max-w-sm items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty) saveName.mutate(trimmed || null);
        }}
      >
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Your name"
          aria-label="Name"
          disabled={saveName.isPending}
        />
        <Button type="submit" disabled={!dirty || saveName.isPending}>
          {saveName.isPending ? "Saving…" : "Save"}
        </Button>
      </form>
      {saveName.error ? (
        <p className="mt-2 text-sm text-destructive">
          {(saveName.error as Error).message}
        </p>
      ) : null}
    </section>
  );
}

/** Read-only "how you're logged in" — provider + the account email. */
function LoginSection({
  email,
  image,
  provider,
}: {
  email: string;
  image: string | null;
  provider: "google" | "dev" | undefined;
}) {
  const initial = email.trim().charAt(0).toUpperCase() || "?";
  return (
    <section>
      <h2 className="text-sm font-semibold">Signed in</h2>
      <div className="mt-3 flex items-center gap-3 rounded-md border border-border p-3">
        {image ? (
          <img
            src={image}
            alt=""
            referrerPolicy="no-referrer"
            className="h-10 w-10 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground">
            {initial}
          </span>
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{email}</div>
          <div className="text-xs text-muted-foreground">
            via {providerLabel(provider)}
          </div>
        </div>
      </div>
    </section>
  );
}

/** The additional email addresses a member may email their tenants from. */
function EmailsSection({
  primary,
  additional,
  pending,
}: {
  primary: string;
  additional: string[];
  pending: string[];
}) {
  const { startAddress } = useProfileActions();
  const [value, setValue] = useState("");
  // Dev-only: the server hands back the code it "emailed" when there's no mail
  // binding, so the flow is usable locally. Keyed by address.
  const [devCodes, setDevCodes] = useState<Record<string, string>>({});

  return (
    <section>
      <h2 className="text-sm font-semibold">Email addresses</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Heath answers mail from any of these. Add another address you send from
        and we'll email it a code to confirm you own it.
      </p>

      <ul className="mt-3 flex flex-col gap-1">
        <li className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
          <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">{primary}</span>
          <span className="shrink-0 text-xs text-muted-foreground">
            Primary
          </span>
        </li>
        {additional.map((email) => (
          <li
            key={email}
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
          >
            <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{email}</span>
            <RemoveButton email={email} />
          </li>
        ))}
        {pending.map((email) => (
          <li
            key={email}
            className="rounded-md border border-dashed border-border px-3 py-2 text-sm"
          >
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-muted-foreground">
                {email}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                Pending
              </span>
              <RemoveButton email={email} label={`Cancel ${email}`} />
            </div>
            <PendingConfirm email={email} devCode={devCodes[email]} />
          </li>
        ))}
      </ul>

      <form
        className="mt-3 flex max-w-sm items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const email = value.trim();
          if (!email) return;
          startAddress.mutate(email, {
            onSuccess: (r) => {
              setValue("");
              if (r.devCode) {
                setDevCodes((prev) => ({ ...prev, [email]: r.devCode! }));
              }
            },
          });
        }}
      >
        <Input
          type="email"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="name@example.com"
          aria-label="Add an email address"
          disabled={startAddress.isPending}
        />
        <Button
          type="submit"
          variant="outline"
          disabled={!value.trim() || startAddress.isPending}
        >
          {startAddress.isPending ? "Sending…" : "Add"}
        </Button>
      </form>
      {startAddress.error ? (
        <p className="mt-2 text-sm text-destructive">
          {(startAddress.error as Error).message}
        </p>
      ) : null}
    </section>
  );
}

/** Trash button shared by verified + pending rows. */
function RemoveButton({ email, label }: { email: string; label?: string }) {
  const { removeAddress } = useProfileActions();
  return (
    <button
      type="button"
      onClick={() => removeAddress.mutate(email)}
      disabled={removeAddress.isPending}
      className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-50"
      aria-label={label ?? `Remove ${email}`}
      title={label ?? `Remove ${email}`}
    >
      <Trash2 className="h-4 w-4" />
    </button>
  );
}

/** Code-entry row for a pending address. */
function PendingConfirm({
  email,
  devCode,
}: {
  email: string;
  devCode?: string;
}) {
  const { verifyAddress } = useProfileActions();
  const [code, setCode] = useState("");
  return (
    <form
      className="mt-2 flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        const trimmed = code.trim();
        if (trimmed) verifyAddress.mutate({ email, code: trimmed });
      }}
    >
      <Input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder={devCode ? `Dev code: ${devCode}` : "Enter code"}
        aria-label={`Confirmation code for ${email}`}
        className="h-8 max-w-[12rem] font-mono text-xs"
        disabled={verifyAddress.isPending}
      />
      <Button
        type="submit"
        size="sm"
        disabled={!code.trim() || verifyAddress.isPending}
      >
        {verifyAddress.isPending ? "Confirming…" : "Confirm"}
      </Button>
      {verifyAddress.error ? (
        <span className="text-xs text-destructive">
          {(verifyAddress.error as Error).message}
        </span>
      ) : null}
    </form>
  );
}
